import { Button, Center, Stack, Switch } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { captureMessage } from '@sentry/react';
import classNames from 'classnames/bind';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { ExcludedAccounts } from 'modules/Settings/components/ExcludedAccounts';
import { ExcludedDebtors } from 'modules/Settings/components/ExcludedDebtors';
import { useEffect, useState } from 'react';
import type { RouteChildrenProps } from 'react-router';
import { isDefined } from 'remeda';
import { useLoadChiftConfigurationLink } from 'shared/hooks/use-load-chift-configuration-link';
import { useLoadCompanyConfiguration } from 'shared/hooks/use-load-company-configuration';
import {
  type ChiftConfigurationVariables,
  ChiftConfigurationVariablesSchema,
  useUpdateChiftConfiguration,
} from 'shared/hooks/use-update-chift-configuration';

import { INTEGRATIONS_ROUTE, Integration } from '../../Integrations.constants';
import styleIdentifiers from '../Integration.module.scss';
import { useGetIntegrationRights } from '../Integration.utils';
import { IntegrationHeader } from '../IntegrationHeader';

const styles = classNames.bind(styleIdentifiers);

export const Chift = ({ history: { replace } }: RouteChildrenProps) => {
  const { t } = useTranslation();
  const { company, refetchCompany } = useLoadCompanyConfiguration();
  const { updateChiftConfiguration } = useUpdateChiftConfiguration();
  const getIntegrationRights = useGetIntegrationRights();

  const { isActive, canActivate, isConnected } = getIntegrationRights(Integration.CHIFT);

  const [isImportInvoicesChecked, setIsImportInvoicesChecked] = useState(
    company?.tp_accounting.use_import,
  );
  const [shouldFetchChiftLink, setShouldFetchChiftLink] = useState(false);

  const { chiftConfigurationLink, isChiftConfigurationLinkFetching } =
    useLoadChiftConfigurationLink({
      enabled: shouldFetchChiftLink,
    });

  useEffect(() => {
    if (chiftConfigurationLink?.link && shouldFetchChiftLink) {
      window.open(chiftConfigurationLink.link, '_blank');
      setShouldFetchChiftLink(false);
      refetchCompany();
    }
  }, [chiftConfigurationLink, shouldFetchChiftLink, refetchCompany]);

  useEffect(() => {
    if (!canActivate && !isActive) {
      replace(INTEGRATIONS_ROUTE);
    }
  }, [canActivate, isActive, replace]);

  const form = useForm<ChiftConfigurationVariables>({
    initialValues: {
      tp_accounting_automatic_sync: company?.tp_accounting.automatic_sync || false,
      tp_accounting_issue_date_limit: company?.tp_accounting.issue_date_limit
        ? dayjs(company.tp_accounting.issue_date_limit).format('YYYY-MM-DD')
        : null,
      fetch_pdf: company?.tp_accounting.additional_parameters?.fetch_pdf || false,
      fetch_raw_data: company?.tp_accounting.additional_parameters?.fetch_raw_data || false,
      unposted_allowed: company?.tp_accounting.additional_parameters?.unposted_allowed || false,
      daily_pdfs_sync: company?.tp_accounting.additional_parameters?.daily_pdfs_sync || false,
    },
    validate: {
      tp_accounting_issue_date_limit: (value, values) => {
        if (values.use_chift_import_integration && !value) {
          return t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.ERROR_MISSING_DATE);
        }
        return null;
      },
    },
  });

  form.watch('use_chift_import_integration', ({ value }) => {
    if (isDefined(value)) setIsImportInvoicesChecked(value);
    if (!value) form.setFieldValue('tp_accounting_issue_date_limit', null);
  });

  const handleSubmit = (values: typeof form.values) => {
    form.setSubmitting(true);
    const safeParse = ChiftConfigurationVariablesSchema.safeParse(values);

    if (!safeParse.success) {
      captureMessage(
        'Zod error while parsing reminders response. Zod error attached to sentry event.',
        { extra: { zodError: safeParse.error } },
      );
      notifications.show({
        title: 'Error',
        message: 'An error occurred while loading the reminders. Our team has been notified.',
        color: 'red',
      });
      throw safeParse.error;
    }
    updateChiftConfiguration(safeParse.data, { onSuccess: () => form.setSubmitting(false) });
  };

  if (!company?.tp_accounting) {
    return (
      <div className={styles('loading')}>
        <div>{t(i18nKeys.LOADING)}</div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <>
        <PageTitle>
          <PageTitle.Back />
          {Integration.CHIFT}
          <PageTitle.Actions>
            <IntegrationHeader name={Integration.CHIFT} />
          </PageTitle.Actions>
        </PageTitle>
        <Center>
          <Button
            loading={isChiftConfigurationLinkFetching}
            onClick={() => setShouldFetchChiftLink(true)}
          >
            {t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.CONNECT_CHIFT)}
          </Button>
        </Center>
      </>
    );
  }

  return (
    <>
      <PageTitle>
        <PageTitle.Back />
        {Integration.CHIFT}
        <PageTitle.Actions>
          <IntegrationHeader name={Integration.CHIFT} />
        </PageTitle.Actions>
      </PageTitle>
      <Stack>
        <Panel
          footer={
            <Button
              disabled={!form.isDirty()}
              type="submit"
              form="chift-form"
              loading={form.submitting}
            >
              {t(i18nKeys.SAVE)}
            </Button>
          }
        >
          <form id="chift-form" onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="lg">
              <Switch
                {...form.getInputProps('use_chift_import_integration', { type: 'checkbox' })}
                checked={isImportInvoicesChecked}
                disabled={form.submitting}
                label={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.IMPORT_INVOICES)}
              />
              {isImportInvoicesChecked && (
                <DatePickerInput
                  ml="md"
                  {...form.getInputProps('tp_accounting_issue_date_limit')}
                  w="30%"
                  required={isImportInvoicesChecked}
                  disabled={form.submitting}
                  placeholder={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.DATEPICKER_PLACEHOLDER)}
                  label={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.IMPORT_INVOICES_FROM)}
                />
              )}
              <Switch
                {...form.getInputProps('tp_accounting_automatic_sync', { type: 'checkbox' })}
                label={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.AUTOMATIC_SYNC)}
                disabled={form.submitting}
              />
              <Switch
                {...form.getInputProps('fetch_pdf', { type: 'checkbox' })}
                label={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.FETCH_PDF)}
                disabled={form.submitting}
              />
              <Switch
                {...form.getInputProps('fetch_raw_data', { type: 'checkbox' })}
                label={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.FETCH_RAW_DATA)}
                disabled={form.submitting}
              />
              <Switch
                {...form.getInputProps('unposted_allowed', { type: 'checkbox' })}
                label={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.UNPOSTED_ALLOWED)}
                disabled={form.submitting}
              />
              <Switch
                {...form.getInputProps('daily_pdfs_sync', { type: 'checkbox' })}
                label={t(i18nKeys.SETTINGS.INTEGRATIONS.CHIFT.DAILY_PDFS_SYNC)}
                disabled={form.submitting}
              />
            </Stack>
          </form>
        </Panel>
        <ExcludedDebtors />
        <ExcludedAccounts />
      </Stack>
    </>
  );
};
